import { useState } from 'react'
import { Plus, Settings } from 'lucide-react'
import { useDashboardStore } from '../stores/dashboardStore'
import { tabAPI } from '../services/api'
import toast from 'react-hot-toast'
import TabSettingsModal from './TabSettingsModal'

const ICON_MAP: Record<string, string> = {
  home: '🏠',
  briefcase: '💼',
  users: '👥',
  star: '⭐',
  calendar: '📅',
  book: '📚',
  music: '🎵',
  game: '🎮',
  shopping: '🛒',
  plane: '✈️',
  heart: '❤️',
  code: '💻',
  file: '📄',
}

export default function DashboardTabs() {
  const { tabs, currentTabId, currentDashboardId, setCurrentTab, setTabs } = useDashboardStore()
  const [editingTab, setEditingTab] = useState<any>(null)

  const handleAddTab = async () => {
    if (!currentDashboardId) return

    try {
      const { data } = await tabAPI.create(currentDashboardId, {
        name: `Tab ${Array.isArray(tabs) ? tabs.length + 1 : 1}`,
        icon: 'file',
        position: Array.isArray(tabs) ? tabs.length : 0,
      })

      if (data && data.id) {
        setTabs(Array.isArray(tabs) ? [...tabs, data] : [data])
        toast.success('Tab created!')
      } else {
        console.error('Created tab missing ID:', data)
        toast.error('Tab created but missing ID')
      }
    } catch (error) {
      console.error('Failed to create tab:', error)
      toast.error('Failed to create tab')
    }
  }

  const handleRefreshTabs = async () => {
    if (!currentDashboardId) return
    try {
      const { data } = await tabAPI.getByDashboard(currentDashboardId)
      setTabs(data)
    } catch (error) {
      console.error('Failed to refresh tabs')
    }
  }

  const handleDeleteTab = async () => {
    if (!currentDashboardId) return
    
    try {
      // Refresh tabs
      const { data } = await tabAPI.getByDashboard(currentDashboardId)
      setTabs(data)
      
      // If the deleted tab was the current one, switch to the first available tab
      if (data.length > 0) {
        const deletedTabStillExists = data.some((tab: any) => tab.id === currentTabId)
        if (!deletedTabStillExists) {
          setCurrentTab(data[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to refresh tabs after deletion')
    }
  }

  const handleDuplicateTab = async () => {
    if (!currentDashboardId) return
    
    try {
      // Refresh tabs
      const { data } = await tabAPI.getByDashboard(currentDashboardId)
      setTabs(data)
      
      // Switch to the newly duplicated tab (it will be the last one)
      if (data.length > 0) {
        const newTab = data[data.length - 1]
        setCurrentTab(newTab.id)
      }
    } catch (error) {
      console.error('Failed to refresh tabs after duplication')
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 overflow-x-auto">
        {Array.isArray(tabs) ? tabs.map((tab) => (
          <div key={tab.id} className="relative group">
            <button
              onClick={() => setCurrentTab(tab.id)}
              className={`px-3 py-1 rounded text-sm transition whitespace-nowrap ${
                currentTabId === tab.id
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.icon && <span className="mr-1">{ICON_MAP[tab.icon] || tab.icon}</span>}
              {tab.name}
            </button>
            
            {currentTabId === tab.id && tab.id && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (tab.id) {
                    setEditingTab(tab)
                  }
                }}
                className="absolute -top-1.5 -right-1.5 p-1 bg-white border border-gray-300 rounded-full opacity-0 group-hover:opacity-100 transition hover:bg-gray-50"
                title="Tab Settings"
              >
                <Settings size={12} />
              </button>
            )}
          </div>
        )) : null}

        <button
          onClick={handleAddTab}
          className="px-3 py-1 text-sm bg-white text-gray-700 rounded hover:bg-gray-100 transition flex items-center gap-1"
        >
          <Plus size={14} />
          Add Tab
        </button>
      </div>

      {editingTab && (
        <TabSettingsModal
          tab={editingTab}
          onClose={() => setEditingTab(null)}
          onSave={handleRefreshTabs}
          onDelete={handleDeleteTab}
          onDuplicate={handleDuplicateTab}
        />
      )}
    </>
  )
}

